import type { RoomAccess } from "@/engine/state/roomAccess";
import { lookupRoomAccess } from "./roomRecord";

/**
 * What can be found out about a room before anybody asks to go in.
 *
 * A code is typed long before it is pressed, and the two things the press then
 * waits for — whether the code opens a room, and where the TURN servers are —
 * do not need the press at all. Fetching them while the visitor is still
 * reading the screen turns a round trip into a cache read.
 *
 * A prefetch is a guess: it is never awaited on the typing path, it never
 * refuses anything on its own, and a stale one is simply asked again.
 */

/** How long a prefetched answer is trusted before it is asked again. */
export const PREFETCH_FRESH_MS = 30_000;
/** Below this, a code is still being typed and asking is just noise. */
export const PREFETCH_MIN_CODE = 4;

export interface PrefetchDeps {
  lookup?: (code: string) => Promise<RoomAccess>;
  warmIce?: (code: string) => Promise<unknown>;
  now?: () => number;
}

interface Entry {
  at: number;
  access: Promise<RoomAccess>;
}

const entries = new Map<string, Entry>();

const normalise = (code: string) => code.trim().toLowerCase();

const defaults = (deps: PrefetchDeps) => ({
  lookup: deps.lookup ?? ((code: string) => lookupRoomAccess({ code })),
  // Fetched with the first code worth warming for: the way in is drawn on
  // every page's first paint, and the loader brings the transport's rate
  // limit with it (#865).
  warmIce:
    deps.warmIce ??
    (async (code: string) =>
      (await import("@/adapters/cloudflare/iceServers")).fetchIceServers(code)),
  now: deps.now ?? Date.now,
});

/**
 * Starts finding out about a code, and returns nothing: the caller is typing,
 * not waiting.
 */
export function prefetchRoom(code: string, deps: PrefetchDeps = {}): void {
  const trimmed = normalise(code);
  if (trimmed.length < PREFETCH_MIN_CODE) return;
  const { lookup, warmIce, now } = defaults(deps);
  const existing = entries.get(trimmed);
  if (existing && now() - existing.at < PREFETCH_FRESH_MS) return;
  entries.set(trimmed, {
    at: now(),
    // A failed prefetch answers "unknown", which is what lets the visitor in.
    // Kept on purpose after #616, where a route that was down opened every
    // mistyped code: `room-lifecycle`'s "The lookup cannot be made" is what
    // this obeys, and the cost of the other choice is a backend hiccup shutting
    // the door on the people already standing in a room that is plainly
    // running. What #616 changed is that the failure is no longer silent —
    // `roomRecord`'s `lookupFailed` says it out loud.
    access: Promise.resolve(lookup(trimmed)).catch((): RoomAccess => "unknown"),
  });
  // TURN credentials are per room and cached by the loader itself.
  void warmIce(trimmed).catch(() => null);
}

/**
 * Whether a code opens a room, taken from the prefetch when it is still fresh
 * and asked for the first time when it is not.
 */
export async function prefetchedRoomAccess(
  code: string,
  deps: PrefetchDeps = {},
): Promise<RoomAccess> {
  const trimmed = normalise(code);
  const { lookup, now } = defaults(deps);
  const existing = entries.get(trimmed);
  if (existing && now() - existing.at < PREFETCH_FRESH_MS)
    return existing.access;
  prefetchRoom(trimmed, deps);
  return (
    entries.get(trimmed)?.access ??
    // Too short to be worth prefetching, so it is asked here — and a failure
    // is still the answer that lets the visitor in.
    Promise.resolve(lookup(trimmed)).catch((): RoomAccess => "unknown")
  );
}

export function resetRoomPrefetch() {
  entries.clear();
}
